import { useEffect } from "react";
import { Search, Clock, Shield, Phone, Users, Home } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroTitleRotator from "@/components/HeroTitleRotator";
import SEO from "@/components/SEO";
import { localBusinessSchema, websiteSchema } from "@/lib/structured-data";

export default function HomePage() {
  
  // Scroll animation effect
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    // Observe all elements with scroll-animate class
    const animateElements = document.querySelectorAll('.scroll-animate');
    animateElements.forEach((el) => observer.observe(el));

    return () => {
      animateElements.forEach((el) => observer.unobserve(el));
    };
  }, []);



  return (
    <div className="min-h-screen">
      <SEO
        title="Property Search Experts - Conveyancing Searches"
        description="Whitefield Legal Services - Expert property searches for conveyancers, estate agents, and auction houses. 7-10 day guaranteed turnaround vs 142+ day industry average."
        keywords="conveyancing searches, property searches, estate agent searches, local authority searches, environmental searches, fast property searches"
        canonicalUrl="/"
        structuredData={[localBusinessSchema, websiteSchema]}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-40 pb-32 bg-wl-navy overflow-hidden">
        {/* Video Background */}
        <video 
          autoPlay 
          loop 
          muted 
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        >
          <source src="/home-back.mp4" type="video/mp4" />
          {/* Fallback for browsers that don't support video */}
          <div 
            className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: 'url(/city-map.jpg)' }}
          ></div>
        </video>
        
        {/* Single overlay for darkening */}
        <div className="absolute inset-0 bg-wl-navy/80"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white/20">
            <HeroTitleRotator />
            
            <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
              Independent property search experts<br />
              serving conveyancers for 20 years.<br />
              Personal service, fast turnaround,<br />
              nationally trusted.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/contact"
                className="bg-white/20 hover:bg-white/30 text-white border border-white/30 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 backdrop-blur-sm flex items-center justify-center gap-2"
              >
                <Search className="w-5 h-5" />
                Book A Demo
              </a>
              <a 
                href="tel:01923620240"
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Call Us Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Needs Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-4">
              Why Conveyancers Choose Whitefield Legal
            </h2>
            <p className="text-xl text-wl-gray max-w-3xl mx-auto">
              We understand the unique challenges facing conveyancing lawyers. 
              Here's how we solve them.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-blue hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-wl-blue" />
              </div>
              <h3 className="text-xl font-semibold text-wl-navy mb-4">When You Need Rapid Turnaround</h3>
              <p className="text-wl-gray mb-4">
                <span className="font-semibold text-wl-green">Searches from as fast as 48 hours (Expedited Service)</span> vs our competitors. 
                Same-day urgent service available for some searches.
              </p>
              <ul className="text-sm text-wl-gray space-y-2">
                <li>• <span className="font-semibold">5 working days for many searches</span></li>
                <li>• No call centres!</li>
                <li>• Speak directly with experts</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-green hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <Search className="w-6 h-6 text-wl-green" />
              </div>
              <h3 className="text-xl font-semibold text-wl-navy mb-4">You're Looking for an Experienced Partner</h3>
              <p className="text-wl-gray mb-4">
                20+ years serving conveyancers nationwide. 
                Personal service where you're never just a number.
              </p>
              <ul className="text-sm text-wl-gray space-y-2">

                <li>• Personal account management</li>
                <li>• Local knowledge, national coverage</li>
                <li>• Get expedited timelines once we understand your needs.</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-navy hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-wl-navy" />
              </div>
              <h3 className="text-xl font-semibold text-wl-navy mb-4">When Compliance is Key</h3>
              <p className="text-wl-gray mb-4">
                Standard indemnity insurance of £2 million, with enhanced limits available on request for high-value or complex developments. 
                fully compliant with all regulations.
              </p>
              <ul className="text-sm text-wl-gray space-y-2">
                <li>• £2M indemnity insurance</li>
                <li>• PCCB registered</li>
                <li>• Search Code compliant</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-green hover:shadow-xl transition-shadow scroll-animate">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <Phone className="w-6 h-6 text-wl-green" />
              </div>
              <h3 className="text-xl font-semibold text-wl-navy mb-4">You Want Technology That Actually Works</h3>
              <p className="text-wl-gray mb-4">
                Modern platform with real-time updates, 
                integrated with your existing systems.
              </p>
              <ul className="text-sm text-wl-gray space-y-2">
                <li>• Real-time status updates</li>
                <li>• System integrations</li>
                <li>• Digital delivery</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-4">
              Complete Search Solutions
            </h2>
            <p className="text-xl text-wl-gray max-w-3xl mx-auto">
              From local authority searches to environmental reports, 
              we provide everything you need for thorough property due diligence.
            </p>
          </div>

          {/* Customer-Focused Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            <div className="text-center p-6 bg-gradient-to-br from-wl-blue/5 to-wl-blue/10 rounded-xl border border-wl-blue/20 hover:shadow-lg transition-all scroll-animate">
              <div className="w-14 h-14 bg-wl-blue rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-wl-navy mb-3">Conveyancing Solicitors</h3>
              <p className="text-wl-gray text-sm">
                Fast, accurate searches with a personal, expert-led service. No call centres — just specialists who understand your transaction and deliver tailored search packs to meet your exact needs.
              </p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-wl-green/5 to-wl-green/10 rounded-xl border border-wl-green/20 hover:shadow-lg transition-all scroll-animate">
              <div className="w-14 h-14 bg-wl-green rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-wl-navy mb-3">Auction Houses</h3>
              <p className="text-wl-gray text-sm">
                Fast-Track Searches for Urgent Completions
              </p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-wl-navy/5 to-wl-navy/10 rounded-xl border border-wl-navy/20 hover:shadow-lg transition-all scroll-animate">
              <div className="w-14 h-14 bg-wl-navy rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-wl-navy mb-3">Commercial / Developers</h3>
              <p className="text-wl-gray text-sm">
              Experts in providing accurate, transaction-specific reports for your complex commercial transactions
              </p>
            </div>
            
            <div className="text-center p-6 bg-gradient-to-br from-wl-blue/5 to-wl-green/5 rounded-xl border border-wl-blue/20 hover:shadow-lg transition-all scroll-animate">
              <div className="w-14 h-14 bg-gradient-to-r from-wl-blue to-wl-green rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-wl-navy mb-3">Estate Agents</h3>
              <p className="text-wl-gray text-sm">
                Upfront search packs to accelerate transactions. Reduce fall-throughs and get paid faster.
              </p>
            </div>

            <div className="text-center p-6 bg-gradient-to-br from-wl-green/5 to-wl-navy/5 rounded-xl border border-wl-green/20 hover:shadow-lg transition-all scroll-animate">
              <div className="w-14 h-14 bg-gradient-to-r from-wl-green to-wl-navy rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-wl-navy mb-3">Homeowners Selling</h3>
              <p className="text-wl-gray text-sm">
                Get your property searches ready in advance. Provide essential information to buyers early, prevent surprises, and expedite your sale with confidence. No conveyancer required.
              </p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors scroll-animate">
              <div className="w-16 h-16 bg-wl-blue rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-wl-navy mb-4">Local Authority Searches</h3>
              <p className="text-wl-gray">
                Comprehensive regulated searches, search code compliant accepted by 99.9 % of Lenders and Official Local Authority Searches
              </p>
            </div>
            
            <div className="text-center p-8 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors scroll-animate">
              <div className="w-16 h-16 bg-wl-green rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-wl-navy mb-4">Environmental Reports</h3>
              <p className="text-wl-gray">
                Detailed environmental risk assessments including contamination, 
                flooding, and ground stability analysis.
              </p>
            </div>
            
            <div className="text-center p-8 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors scroll-animate">
              <div className="w-16 h-16 bg-wl-navy rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-wl-navy mb-4">Specialist Searches</h3>
              <p className="text-wl-gray">
                Water & drainage, chancel liability, Title and Legal Indemnity Insurance and other specialist property searches. Contact us for your requirements
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12 scroll-animate">
            <a 
              href="/services" 
              className="inline-block bg-wl-green hover:bg-wl-green/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              View All Services
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate">
            <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-4">
              What Our Clients Say
            </h2>
            <p className="text-xl text-wl-gray">
              Trusted by conveyancers and auction houses across the UK
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-blue scroll-animate">
              <div className="mb-6">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>★</span>
                  ))}
                </div>
                <p className="text-wl-gray italic">
                  "Outstanding, quick and professional service since 2013. Friendly advice available anytime, 
                  and urgent searches are delivered swiftly. They've made my work stress-free for over a decade."
                </p>
              </div>
              <div className="font-semibold text-wl-navy">Vijaya Ratnam</div>
              <div className="text-wl-gray text-sm">Starck Uberoi</div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-green scroll-animate">
              <div className="mb-6">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>★</span>
                  ))}
                </div>
                <p className="text-wl-gray italic">
                  "None have the quality of personal service and ease of use as Whitefield. 
                  A simple email with a plan is all we need. Responsive, proactive, and readily willing to assist."
                </p>
              </div>
              <div className="font-semibold text-wl-navy">Hitesh Patel</div>
              <div className="text-wl-gray text-sm">Harrow Law Practice</div>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg border-l-4 border-wl-navy scroll-animate">
              <div className="mb-6">
                <div className="flex text-yellow-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span key={i}>★</span>
                  ))}
                </div>
                <p className="text-wl-gray italic">
                  "We can pick up the phone and speak to someone straight away. 
                  Exceptional customer service, issues dealt with promptly, and very user-friendly platform."
                </p>
              </div>
              <div className="font-semibold text-wl-navy">Nadia Karaivanova</div>
              <div className="text-wl-gray text-sm">CVS</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center scroll-animate">
          <h2 className="text-3xl md:text-4xl font-bold text-wl-navy mb-6">
            Ready to Experience the Whitefield Difference?
          </h2>
          <p className="text-xl text-wl-gray mb-8">
            Join hundreds of conveyancers who trust us with their property searches. 
            Get started today with a free consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/contact" 
              className="bg-wl-green hover:bg-wl-green/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors"
            >
              Get A Free Quote
            </a>
            <a 
              href="tel:01923620240"
              className="bg-wl-blue hover:bg-wl-blue/90 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Call 01923 620240/41
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
} 